// ─── Every dashboard action, attributed to the person who took it ────────────
// (owner, 2026-10-13: a collaborator's page "needs a view activities button
// when you see a data table of ALL their actions on wavelength dashboard").
//
// Before this, only the Commerce Control suite wrote audit rows, so a person's
// trail had gaps everywhere else. Now every save that leaves the dashboard
// (the ERP API, the register's write actions, the online store's write
// actions) adds one row to the append-only audit-log once it succeeds: who,
// when, what was done (created / updated / deleted / an action) and to which
// record. The record's body is NEVER copied: a save can carry card keys,
// passwords, customer details or a message's text, so the row names the
// record and the change, nothing more.
//
// The person is the one signed in (Cognito), never an impersonated profile: an
// admin who switched to another user is still the one acting, and the row
// says whose view they were in.

let actor = null;       // { email, userId, name, role, business, asEmail }
let sender = null;      // (row) => Promise, set by erpApi (no import cycle)

/** Who is acting now. null (signed out) records nothing. */
export function setActivityActor(next) {
    actor = next && next.email ? next : null;
}

/** How a row is saved: erpApi registers its own POST to /audit-log. */
export function setActivitySender(fn) {
    sender = typeof fn === 'function' ? fn : null;
}

// What each route holds, in words.
const RESOURCES = {
    'vendors': 'Vendor',
    'products': 'Product',
    'raw-materials': 'Raw Material',
    'yield': 'Yield Batch',
    'purchase-orders': 'Purchase Order',
    'invoices': 'Invoice',
    'storage': 'Storage Location',
    'label-presets': 'Label Format',
    'barcode-formats': 'Barcode Format',
    'staffs': 'Staff',
    'package-configs': 'Package Station',
    'kiosk-configs': 'Kiosk',
    'scale-configs': 'Scale',
    'collaboration': 'User',
    'locations': 'Location',
    'wholesale-customers': 'Wholesale Customer',
    'wholesale-orders': 'Wholesale Order',
    'wholesale-applications': 'Wholesale Application',
    'wholesale-product-pricing': 'Wholesale Price',
    'organizations': 'Organization',
    'settings': 'Settings',
    'gmail': 'Email',
    'package': 'Package',
    'email-audience': 'Email Audience',
    'support-tickets': 'Support Ticket',
    'admin-records': 'Admin Record',
    'bank-statements': 'Bank Statement',
    'cash-sessions': 'Cash Drawer',
    'deposits': 'Deposit',
    'payouts': 'Payout',
    'bank-transactions': 'Bank Transaction',
    'refunds': 'Refund',
    'remote-refunds': 'Card Refund',
    'gift-certificates': 'Gift Card',
    'qb-postings': 'QuickBooks Posting',
    'receiving-reports': 'Receiving Report',
    'dashboard-overrides': 'Dashboard Record',
};

// A dashboard-overrides row says what it is in `kind` or `type`.
const OVERRIDE_KINDS = {
    'dm': 'Message',
    'reorder-setting': 'Reorder Policy',
    'bank_statement': 'Bank Statement',
    'bank_match': 'Reconciliation Match',
    'bank_account': 'Bank Account',
};

// Saves that are really a named action on their route.
const ROUTE_ACTIONS = {
    'POST gmail/send': 'Sent Email',
    'POST gmail/mailbox': 'Changed Sending Email',
    // Queueing one names the sale (order_id); acting on one reads "Cancel,
    // Card Refund" from the body's action.
    'POST remote-refunds/': 'Queued Card Refund',
    'POST wholesale-orders/:id': 'Sent Invoice, Wholesale Order',
};

// "upsert" is a storage word; to a reader the record was saved.
const WORDS = { upsert: 'Save' };
const titleCase = (s) => String(s || '')
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .map((w) => WORDS[w.toLowerCase()] || (w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');

const parseBody = (body) => {
    if (body == null) return {};
    if (typeof body === 'object' && !(body instanceof URLSearchParams)) return body;
    if (body instanceof URLSearchParams) return Object.fromEntries(body.entries());
    const text = String(body);
    try { return JSON.parse(text) || {}; } catch { /* a form body */ }
    try { return Object.fromEntries(new URLSearchParams(text).entries()); } catch { return {}; }
};

// An action name that only reads (a POST-shaped list or lookup).
const READ_ACTION = /^(list|get|fetch|read|search|find|lookup|load|doc_url|doc_presign|preview|check|count|status)(_|-|$)/i;
// A store action name that writes.
const WRITE_ACTION = /^(set|create|add|remove|delete|place|record|send|update|upsert|void|refund|charge|cancel|approve|decline|publish|save|mark|issue|apply|link|unlink|unlock|lock|import|sync|subscribe|unsubscribe|disconnect|reset)(_|-|$)/i;

const VERBS = { POST: 'Created', PUT: 'Updated', PATCH: 'Updated', DELETE: 'Deleted' };

// The record a save touched: the path's own id, else an id the body names.
function entityIdOf(segments, body) {
    const fromPath = segments.slice(1).filter((s) => s && !/^\?/.test(s)).map(decodeURIComponent)[0];
    if (fromPath) return fromPath.split('?')[0];
    for (const key of ['name', 'override_id', 'order_id', 'uuid', 'id', 'rm_id', 'product_id', 'customer_id', 'note_id', 'box_id', 'business_id']) {
        const v = body?.[key];
        if (v != null && v !== '' && typeof v !== 'object') return String(v);
    }
    return '';
}

/**
 * What a save did, in words, or null when it is not an action worth a row
 * (a read sent as a POST, or the audit log's own write).
 *   { action, action_label, entity_type, entity_id, route }
 * `kind` is 'erp' (a REST path), 'kiosk' or 'store' (an action name).
 */
export function describeWrite({ kind = 'erp', path = '', method = 'POST', body } = {}) {
    const b = parseBody(body);
    if (kind === 'kiosk' || kind === 'store') {
        const name = String(path || b.action || '').trim();
        if (!name || READ_ACTION.test(name)) return null;
        if (kind === 'store' && !WRITE_ACTION.test(name)) return null;
        return {
            action: `dashboard.${kind}`,
            action_label: titleCase(name),
            entity_type: kind === 'kiosk' ? 'Register' : 'Online Store',
            entity_id: entityIdOf([], b),
            route: name,
        };
    }
    const clean = String(path || '').replace(/^https?:\/\/[^/]+/i, '').replace(/^\/(prod|dev)(?=\/)/, '').split('?')[0];
    const segments = clean.split('/').filter(Boolean);
    const resource = segments[0] || '';
    if (!resource || resource === 'audit-log') return null;
    const verb = String(method || 'POST').toUpperCase();
    if (verb === 'GET' || verb === 'HEAD') return null;
    const bodyAction = typeof b.action === 'string' ? b.action : '';
    if (bodyAction && READ_ACTION.test(bodyAction)) return null;
    let noun = RESOURCES[resource] || titleCase(resource);
    if (resource === 'dashboard-overrides') noun = OVERRIDE_KINDS[b.kind] || OVERRIDE_KINDS[b.type] || (b.kind || b.type ? titleCase(b.kind || b.type) : noun);
    const named = ROUTE_ACTIONS[`${verb} ${resource}/${segments[1] || ''}`]
        || (segments[1] ? ROUTE_ACTIONS[`${verb} ${resource}/:id`] : null);
    const label = named || (bodyAction ? `${titleCase(bodyAction)}, ${noun}` : `${VERBS[verb] || 'Saved'} ${noun}`);
    const isNamedRoute = !!ROUTE_ACTIONS[`${verb} ${resource}/${segments[1] || ''}`];
    return {
        action: `dashboard.${named || bodyAction ? 'action' : (verb === 'DELETE' ? 'delete' : verb === 'POST' ? 'create' : 'update')}`,
        action_label: label,
        entity_type: noun,
        entity_id: isNamedRoute ? entityIdOf([], b) : entityIdOf(segments, b),
        route: `${verb} /${segments.join('/')}`,
    };
}

/** The audit row for a save, or null. Exported for tests. */
export function activityRow(request, who = actor, now = new Date()) {
    if (!who?.email) return null;
    const what = describeWrite(request);
    if (!what) return null;
    return {
        action: what.action,
        action_label: what.action_label,
        entity_type: what.entity_type,
        entity_id: what.entity_id,
        before_value: null,
        after_value: null,
        reason: '',
        route: what.route,
        page: typeof window !== 'undefined' ? window.location?.pathname || '' : '',
        user_id: who.userId || who.email,
        user_email: who.email,
        user_name: who.name || '',
        user_role: who.role || '',
        ...(who.asEmail ? { acting_as_email: who.asEmail } : {}),
        ...(who.business ? { organization_uuid: who.business } : {}),
        created_at: now.toISOString(),
    };
}

// A caller that knows what its save means (a physical count: before, after)
// names the next write to that path, and the generic "Updated Raw Material"
// row carries those words instead. It is used once, only for a save to the
// named path, and forgotten after a short while so a save that never went
// through can't hand its words to an unrelated one later.
let pending = null;
const PENDING_MS = 15000;
const NAMED = ['action', 'action_label', 'entity_type', 'entity_id', 'before_value', 'after_value', 'reason'];

/** Name the next successful write to `path` (a substring of the request path). */
export function nextActivity(desc) {
    pending = desc && desc.path ? { ...desc, at: Date.now() } : null;
}

function withPending(row, request) {
    if (!pending) return row;
    if (Date.now() - pending.at > PENDING_MS) { pending = null; return row; }
    if (!String(request?.path || '').includes(pending.path)) return row;
    const named = Object.fromEntries(NAMED.filter((k) => pending[k] != null).map((k) => [k, pending[k]]));
    pending = null;
    return { ...row, ...named };
}

/** Record a save that succeeded. Fire and forget: it never blocks or fails the save. */
export function recordActivity(request) {
    if (!sender) return;
    const built = activityRow(request);
    if (!built) return;
    const row = withPending(built, request);
    try {
        Promise.resolve(sender(row)).catch(() => {});
    } catch { /* never let the trail break a save */ }
}
